import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';

export const AuthContext = createContext();

// Configure axios defaults
axios.defaults.withCredentials = true;
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Update the API_BASE to include the correct path
// const API_BASE = process.env.BACKEND_URL || 'https://10.22.197.135:5001/api';
const API_BASE = process.env.BACKEND_URL ||'https://vc.spectraai.in/vc/api';

// Configure axios instance with CORS settings
const axiosInstance = axios.create({
  baseURL: API_BASE, // Use relative URL with proxy
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add this line to disable SSL certificate validation (only for development!)
process.env.NODE_TLS_REJECT_UNAUTHORIZED = '0';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user data if token exists
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        setAuthToken(token);
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  // Update the useEffect hook that handles OAuth redirects
  useEffect(() => {
    const checkForOAuthRedirect = async () => {
      console.log('Checking for OAuth redirect, hash:', window.location.hash);
      // Check for token in hash regardless of authentication status
      if (window.location.hash.includes('token=')) {
        console.log('Token found in hash, attempting to authenticate');
        const success = await handleOAuthRedirect();
        if (success) {
          console.log('Successfully authenticated with OAuth');
          console.log('Token received and verified successfully');
        } else {
          console.error('OAuth authentication failed');
          console.log('Token verification failed or token not received');
        }
      } else {
        console.log('No token found in redirect URL');
      }
    };
    
    checkForOAuthRedirect();
    
    // Add event listener for hash changes
    const handleHashChange = () => {
      console.log('URL hash changed, rechecking for token');
      checkForOAuthRedirect();
    };
    
    window.addEventListener('hashchange', handleHashChange);
    
    // Clean up event listener
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []); // Empty dependency array means this runs once on mount

  // Set auth token in headers
  const setAuthToken = (token) => {
    if (token) {
      // Set both header formats for compatibility
      axiosInstance.defaults.headers.common['x-auth-token'] = token;
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
      console.log('Auth token set successfully');
      console.log('Authorization header:', axiosInstance.defaults.headers.common['Authorization']);
    } else {
      delete axiosInstance.defaults.headers.common['x-auth-token'];
      delete axiosInstance.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
      console.log('Auth token removed');
    }
  };
  // Register user
  const register = async (formData) => {
    try {
      console.log('Sending registration data:', formData);
      
      const res = await axiosInstance.post('/auth/register', formData);
      console.log('Registration response:', res.data);
      
      if (res.data && res.data.token) {
        const token = res.data.token;
        localStorage.setItem('token', token);
        setAuthToken(token);
        setToken(token);
        setUser(res.data.user);
        setIsAuthenticated(true);
        setError(null);
        return true;
      } else {
        setError('Invalid response from server');
        return false;
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.message || 'Registration failed');
      return false;
    }
  };

  // Login user
  const login = async (formData) => {
    try {
      const res = await axiosInstance.post('/auth/login', formData);
      
      if (res.data && res.data.token) {
        setToken(res.data.token);
        setUser(res.data.user);
        setIsAuthenticated(true);
        setError(null);
        return true;
      } else {
        setError('Invalid response from server');
        return false;
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Login failed');
      return false;
    }
  };

  // Google login - simplified version
  const loginWithGoogle = async () => {
    try {
      // Just open Google OAuth in same window
      window.open(`${API_BASE}/auth/google`, '_self');
      // The redirect handling should be done in a separate component
      return true;
    } catch (err) {
      console.error('Google login error:', err);
      setError('Google login failed');
      return false;
    }
  };
  
  // Add a separate function to handle OAuth redirects
  // Inside handleOAuthRedirect function
  // Improve the handleOAuthRedirect function
  const handleOAuthRedirect = async () => {
    console.log('Checking for OAuth redirect token...');
    console.log('Current URL:', window.location.href);
    
    // Inside handleOAuthRedirect function
    const hashString = window.location.hash;
    console.log('URL hash (raw):', hashString);
    
    let token = null;
    
    // Extract token from hash using URLSearchParams for more reliable parsing
    if (hashString && hashString.includes('token=')) {
      console.log('Token string found in hash');
      // Remove the leading # and parse with URLSearchParams
      const hashParams = new URLSearchParams(hashString.substring(1));
      token = hashParams.get('token');
      console.log('Extracted token using URLSearchParams:', token);
    }
    
    if (token) {
      try {
        console.log('Setting token in localStorage and headers...');
        // Set the token in localStorage and headers first
        setAuthToken(token);
        setToken(token);
        
        console.log('Verifying token with backend...');
        console.log('API_BASE:', API_BASE);
        
        // Add cache-busting parameter and force fresh response
        const response = await axiosInstance.get('/auth/user', {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          },
          params: { _t: new Date().getTime() } // Add timestamp to prevent caching
        });
        
        console.log('Token verification response:', response);
        
        // Check if we have a valid response
        if (response && response.data) {
          const { _id, username, email } = response.data;
          console.log('User verified:', response.data);
          
          setUser(response.data);
          setIsAuthenticated(true);
          setError(null);
          
          // Clear the token from the URL without triggering a page reload
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Navigate to dashboard after successful authentication
          window.location.href = '/#/';
          return true;
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('Token verification failed:', err);
        console.error('Error details:', err.response ? err.response.data : 'No response data');
        console.error('Status code:', err.response ? err.response.status : 'No status code');
        setError('Authentication failed: ' + (err.message || 'Unknown error'));
        // Remove the invalid token
        setAuthToken(null);
        return false;
      }
    }
    return false;
  };

  // Logout user
  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setAuthToken(null);
  };

  // Clear errors
  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthenticated,
        loading,
        user,
        error,
        register,
        login,
        loginWithGoogle,
        logout,
        clearError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};